import { useMemo, useState } from "react"

import type {
  SupplierOnboardingStep,
  SupplierOnboardingStepId,
} from "~/pages/SupplierPortal/Onboarding/constants/supplierOnboardingSteps"

type UseOnboardingStepperArgs = Readonly<{
  initialStepId: SupplierOnboardingStepId
  steps: readonly SupplierOnboardingStep[]
}>

const findStepIndex = (
  steps: readonly SupplierOnboardingStep[],
  stepId: SupplierOnboardingStepId
): number => {
  const index = steps.findIndex((step) => step.id === stepId)
  return index >= 0 ? index : 0
}

export const useOnboardingStepper = ({
  initialStepId,
  steps,
}: UseOnboardingStepperArgs) => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(() =>
    findStepIndex(steps, initialStepId)
  )

  const currentStep = useMemo(
    () => steps[currentStepIndex] ?? steps[0],
    [steps, currentStepIndex]
  )

  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1

  const goToNextStep = (): void => {
    setCurrentStepIndex((previousIndex) =>
      Math.min(previousIndex + 1, steps.length - 1)
    )
  }

  const goToPreviousStep = (): void => {
    setCurrentStepIndex((previousIndex) => Math.max(previousIndex - 1, 0))
  }

  return {
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
  }
}
